import '../collection-action-menu/collection-action-menu.js';
import '../wishlist-collection-head/wishlist-collection-head.js';
import {customElement, property, queryAssignedElements, state} from 'lit/decorators.js';
import {ifDefined} from 'lit/directives/if-defined.js';
import {css, CSSResult, html, LitElement, nothing, PropertyValues, TemplateResult, unsafeCSS,} from 'lit';
import arrowBackIcon from '@breuni-ui/bdk/dist/design-tokens/images/icons/o-ic-16_arrow-left.svg';
import Close from '@breuni-ui/bdk/dist/design-tokens/images/icons/o-ic-24_close.svg';
import heartIcon from '@breuni-ui/bdk/dist/design-tokens/images/icons/f-ic-24_heart.svg';
import {consume, provide} from '@lit/context';
import {
  CollectionTrackingContext,
  Messages,
  Product,
  ProductIdentifierContainer,
  SwitchModelContainer,
  WishlistCollection,
  WishlistMailProductAdded
} from '../../../@types/wishlist.js';
import '../wishlist-product/wishlist-product.js';
import '../product-family/product-family-slider.js';
import '../filter/filter.js';
import '../filter/filter-button.js';
import '../event-icon/event-icon.js';
import '../product-action-menu/product-action-menu.js';
import '../mail-product-added/mail-product-added.js';
import '../size-layer/size-layer.js';
import '../add-to-list-layer/add-to-list-layer.js';
import '../options/options.js';
import styles from './wishlist-container.scss';
import grids from '../../../styles/grids.scss';
import typographyStyles from '../../../styles/responsive-nui-typography.scss';
import {i18nContext} from '../../../scripts/context/i18nContext.js';
import {trackPageView, trackViewCollections, trackViewWishlist} from '../../../scripts/service/cube-tracking-layer.js';
import {FilterMenu} from '../../../scripts/filter/filter-menu.js';
import {filterContext} from '../../../scripts/context/filterContext.js';
import {HeadRenderCase, ProductsRenderCase, WishlistRenderCase} from '../../../scripts/service/wishlist-render-case.js';
import {OptionUIModel} from '../../../scripts/filter/option-ui-model.js';
import {previewAnimationContext, PreviewState} from '../../../scripts/context/previewAnimationContext.js';
import {sizeLayerContext, SizeLayerState} from '../../../scripts/context/sizeLayerContext.js';
import {productFamilyContext, ProductFamilyState} from '../../../scripts/context/productFamilyContext.js';
import {WishlistAppConfig, wishlistAppConfigContext} from '../../../scripts/context/appConfigContext.js';
import {collectionsContext, productsContext} from '../../../scripts/context/wishlistContext.js';
import {ProductIdentifier} from '../../../scripts/service/product-identifier.js';
import {CollectionsState, ProductsState} from '../../../scripts/context/wishlist-state.js';
import {addToListLayerContext, AddToListLayerState} from '../../../scripts/context/addToListLayerContext.js';
import {config} from '../../../scripts/service/config.js';
import {unsafeHTML} from 'lit/directives/unsafe-html.js';
import {addPresentationToIcon} from '../icon/icon-accessibility.js';
import {loadFromDOM} from '../../../scripts/utils/load-from-dom.js';
import {repeat} from 'lit/directives/repeat.js';
import {mediaQuery} from '../../../scripts/service/media-query.js';
import {classMap} from 'lit/directives/class-map.js';

@customElement('ents-wishlist-container')
export class WishlistContainer extends LitElement {
  @property({type: Object, attribute: 'collection'})
  private wishlistCollection?: WishlistCollection;

  @property({type: Boolean, attribute: 'logged-in'})
  private loggedIn: boolean = false;

  @consume({context: i18nContext})
  private messages!: Messages;

  @consume({context: wishlistAppConfigContext})
  private wishlistAppConfig!: WishlistAppConfig;

  @consume({context: collectionsContext, subscribe: true})
  private collectionsState!: CollectionsState;

  @consume({context: productsContext, subscribe: true})
  @property({attribute: false})
  private productsState!: ProductsState;

  @provide({context: filterContext})
  @property({attribute: false})
  private filterMenu!: FilterMenu;

  @provide({context: previewAnimationContext})
  private previewAnimationContext: PreviewState = {
    previewAnimationStarted: false,
    previewAnimationEnded: false,
    startPreviewAnimation: () => {
      this.previewAnimationContext = {...this.previewAnimationContext, previewAnimationStarted: true};
    },
    endPreviewAnimation: () => {
      this.previewAnimationContext = {...this.previewAnimationContext, previewAnimationEnded: true};
    },
  };

  @provide({context: sizeLayerContext})
  private sizeLayerState: SizeLayerState = {
    product: null,
    selectedArticle: null,
    trackingContext: '',
    triggerA2C: false,
    open: false,
    update: (newState) => {
      this.sizeLayerState = {
        ...this.sizeLayerState, ...newState,
      };
    },
    backButtonCallback: undefined,
  };

  @provide({context: productFamilyContext})
  private productFamilyState: ProductFamilyState = {
    sliderData: null,
    open: false,
    beauty: false,
    update: (newState) => {
      this.productFamilyState = {
        ...this.productFamilyState, ...newState,
      };
    },
    backButtonCallback: undefined,
  };

  @provide({context: addToListLayerContext})
  private addToListLayerState: AddToListLayerState = {
    open: false,
    update: (newState) => {
      this.addToListLayerState = {
        ...this.addToListLayerState, ...newState,
      };
    },
    backButtonCallback: undefined,
  };

  @state()
  private modelViewEnabled: boolean = false;

  @queryAssignedElements({slot: 'inlist-newsletter'})
  private inlistNewsletterSlotElements!: HTMLElement[];

  @state()
  private products: Product[] = [];

  @state()
  private wishlistMailProductAdded: WishlistMailProductAdded | null = null;

  @state()
  private renderedCount = 0;

  private chunkSize = 20;

  private scheduleLoadCampFlagsTimeout?: number;

  private readonly SURVEY_VERSION = 'v2';

  private setProductsForCollection() {
    this.products = this.productsState.getProductsToRender(this.wishlistCollection?.id);
  };

  static get styles(): CSSResult[] {
    return [css`${unsafeCSS(styles)}`, css`${unsafeCSS(typographyStyles)}`, css`${unsafeCSS(grids)}`];
  }

  connectedCallback() {
    super.connectedCallback();
    this.setProductsForCollection();

    this.createAndSetFilterMenu();
    window.addEventListener('popstate', () => this.createAndSetFilterMenu());
    document.addEventListener('ents:switch-model-view', this.switchModelView.bind(this));
    document.addEventListener('camp:aktionsflags:loader:ready', () => {
      this.triggerCampEvent();
    }, {once: true});
    document.addEventListener('ents:wishlist-scroll-to-product', this.scrollToProduct.bind(this));
    mediaQuery.on('change:width', this.handleResize);

    this.modelViewEnabled = this.loadModelViewFromLocalStorage();
    this.wishlistMailProductAdded = loadFromDOM('#ents-wishlist-mail-product-added-data');
    setTimeout(() => this.trackPageEvents());
    this.updateProgressiveRenderCount(this.products);
  }

  private trackPageEvents() {
    const trackingType = this.determineTrackingType();
    trackPageView(trackingType.page, this.products.length === 0, this.loggedIn);
    trackViewWishlist(trackingType.context, this.products);
    trackViewCollections(trackingType.context, this.collectionsState.entities, this.productsState);
  }

  private determineTrackingType(): { page: string, context: string } {
    if (this.productsState.isShared()) {
      return {page: 'shared_collection_detail', context: 'collection'};
    } else if (this.wishlistCollection) {
      return {page: 'collection_detail', context: 'collection'};
    } else {
      return {page: 'all_articles', context: 'wishlist'};
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener('popstate', this.createAndSetFilterMenu);
    document.removeEventListener('ents:switch-model-view', this.switchModelView.bind(this));
    document.removeEventListener('ents:wishlist-scroll-to-product', this.scrollToProduct.bind(this));
    mediaQuery.off('change:width', this.handleResize);

    if (this.scheduleLoadCampFlagsTimeout) {
      clearTimeout(this.scheduleLoadCampFlagsTimeout);
    }
  }

  private handleResize = () => {
    this.requestUpdate();
  };

  private handleScheduleLoadCampFlags() {
    if (this.scheduleLoadCampFlagsTimeout) {
      clearTimeout(this.scheduleLoadCampFlagsTimeout);
    }
    this.scheduleLoadCampFlagsTimeout = window.setTimeout(() => {
      // debounce CAMP event to wait for all newly rendered products with a <camp-aktionsflag> element
      this.triggerCampEvent();
      this.scheduleLoadCampFlagsTimeout = undefined;
    }, 50);
  }

  private createAndSetFilterMenu() {
    this.filterMenu = FilterMenu.createMenuFor(this.products);

    // if the promotion filter option is active here, it means the page was loaded with the filter in the URL.
    // remove it if there are 0 matches, because this means that there are no articles with `isInPromotion`
    // set by our backend and so filtering by promotion will not work until the CAMP API call has happened.
    const promotionFilterActive = this.filterMenu.filter.flatMap(f => f.getActiveOptions(false)).some(o => o.id === 'in-promotion');
    if (promotionFilterActive && this.filterMenu.resultCount === 0) {
      this.filterMenu = this.filterMenu.toggleFilterOption(this.products, 'in-promotion', true);
      this.filterMenu = this.filterMenu.applyEditState();
    }
  }

  private onWishlistFilterChangedEvent = (e: CustomEvent<{ optionId: string, allowRemove: boolean }>) => {
    this.filterMenu = this.filterMenu.toggleFilterOption(this.products, e.detail.optionId, e.detail.allowRemove);
  };

  private onWishlistFilterOpen() {
    this.filterMenu = this.filterMenu.open(this.products);
  }

  private onWishlistFilterClose() {
    this.filterMenu = this.filterMenu.close();
  }

  private onWishlistFilterApply = () => {
    this.filterMenu = this.filterMenu.applyEditState();
    this.resetRenderCount();
  };

  private onWishlistFilterRevert = () => {
    this.filterMenu = this.filterMenu.undoEditState(this.products);
  };

  private onWishlistFilterReset = () => {
    this.filterMenu = this.filterMenu.resetAllFilters(this.products);
    this.resetRenderCount();
  };

  private onWishlistFilterResetFilter = (e: CustomEvent<{ filterId: string }>) => {
    this.filterMenu = this.filterMenu.resetFilter(this.products, e.detail.filterId);
    this.resetRenderCount();
  };

  private resetRenderCount = () => {
    this.renderedCount = this.chunkSize;
  };

  private onScrollTop = () => {
    setTimeout(() => {
      const containerTop = this.getBoundingClientRect().top;
      if (containerTop < 0) {
        this.scrollIntoView({behavior: 'instant', block: 'start'});
      }
    }, 50);
  };

  protected renderFilterButton() {
    return html`
      <div class="ents-wishlist-filter-button">
        <ents-filter-button
          @apply="${this.onWishlistFilterApply}"
          @changed="${this.onWishlistFilterChangedEvent}"
          @open="${this.onWishlistFilterOpen}"
          @scroll-top="${this.onScrollTop}"
        ></ents-filter-button>
      </div>
    `;
  }

  private reapplyFilter() {
    this.filterMenu = this.filterMenu.reapplyFilter(this.products);
  }

  private resetAllFilters() {
    this.filterMenu = this.filterMenu.resetAllFilters(this.products);
  }

  private switchModelView(e: CustomEvent<SwitchModelContainer>) {
    this.modelViewEnabled = e.detail.modelViewEnabled;
    window.localStorage.setItem('ents-wishlist-model-view', String(this.modelViewEnabled));
  }

  private loadModelViewFromLocalStorage(): boolean {
    return window.localStorage.getItem('ents-wishlist-model-view') === 'true';
  }

  private renderEmptyCollection() {
    if (this.productsState.entities.length > 0) {
      return html`
        <div data-test-ents-wishlist-collection-empty>
          ${this.messages['ents.wishlist.collection.details.empty']}
        </div>
      `;
    } else {
      return html`
        <div data-test-ents-wishlist-collection-empty-favorites class="ents-wishlist-collection-empty-favorites">
          ${unsafeHTML(this.messages['ents.wishlist.collection.details.emptyFavorites'])}
          <div class="ents-wishlist-collection-empty-favorites__button-container">
            <a href="${config.homepage}">
              <button class="bdk-button" type="button" variant="outline" size="medium">
                <span
                  class="ents-heading ents-text-uppercase">${this.messages['ents.wishlist.collection.details.emptyFavorites.cta']}</span>
              </button>
            </a>
          </div>
        </div>
      `;
    }
  }

  private renderOverfilteredWishlist() {
    const chipsFromAllFiltersSorted = this.filterMenu.filter
      .filter(filter => !filter.isSort)
      .flatMap(filter => filter.getActiveOptions(false))
      .sort((optionA, optionB) => optionA.label(this.messages).localeCompare(optionB.label(this.messages)));
    return html`
      <ents-options class="ents-options-container">
        ${this.renderFilterButton()}
      </ents-options>
      <div class="ents-wishlist-overfiltered"
           @changed="${this.handleOverfilteredChange}">
        <h3 class="ents-copy-medium-bold" data-ents-wishlist-overfiltered-message>
          ${this.messages['ents.wishlist.overfiltered.message']}</h3>
        ${chipsFromAllFiltersSorted.map(option => (this.renderChip(option)))}
        <div>
          <button class="ents-wishlist-overfiltered__reset-all-button bdk-link-primary" size="small"
                  @click="${this.onWishlistFilterReset}"
                  data-test-ents-overfiltered-reset-all-button>
            ${this.messages['ents.wishlist.filter.reset.all']}
          </button>
        </div>
      </div>
    `;
  }

  protected renderWishlist(products: Product[]) {
    return html`
      ${this.renderWishlistGrid(products)}
      <ents-filter
        @apply="${this.onWishlistFilterApply}"
        @revert="${this.onWishlistFilterRevert}"
        @changed="${this.onWishlistFilterChangedEvent}"
        @reset="${this.onWishlistFilterReset}"
        @reset-filter="${this.onWishlistFilterResetFilter}"
        @close="${this.onWishlistFilterClose}"
        @scroll-top="${this.onScrollTop}"
      ></ents-filter>
      <ents-product-action-menu
        data-test-ents-action-menu="product-action-menu-layer"
      >
        <div slot="product-banner">
          <slot name="product-banner"></slot>
        </div>
        <div slot="gwp-banner">
          <slot name="gwp-banner"></slot>
        </div>
      </ents-product-action-menu>
      <ents-size-layer ?logged-in="${this.loggedIn}"></ents-size-layer>
      <ents-product-family-slider></ents-product-family-slider>
      <ents-add-to-list-layer></ents-add-to-list-layer>
    `;
  }

  private renderEventsIcon() {
    if (this.wishlistCollection || this.productsState.isShared()) {
      return nothing;
    }
    return html`
      <ents-event-icon
        ?logged-in="${this.loggedIn}"
      ></ents-event-icon>
    `;
  }

  private renderMailProductAdded() {
    const productAddedOrDuplicate = !!(this.wishlistMailProductAdded && (this.wishlistMailProductAdded.added || this.wishlistMailProductAdded.duplicate));
    return html`
      <ents-wishlist-mail-product-added
        syan="${ifDefined(productAddedOrDuplicate ? this.wishlistMailProductAdded!.syan : undefined)}"
        color-id="${ifDefined(productAddedOrDuplicate ? this.wishlistMailProductAdded!.colorId : undefined)}"
        duplicate="${ifDefined(productAddedOrDuplicate ? this.wishlistMailProductAdded!.duplicate : undefined)}"
        added-message="${ifDefined(productAddedOrDuplicate ? this.messages['ents.wishlist.snackbar.added'] : undefined)}"
        duplicate-message="${ifDefined(productAddedOrDuplicate ? this.messages['ents.wishlist.snackbar.added.duplicate'] : undefined)}"
        preview-image="${ifDefined(productAddedOrDuplicate ? this.wishlistMailProductAdded!.productImage : undefined)}"
        description-text="${ifDefined(productAddedOrDuplicate ? this.wishlistMailProductAdded!.descriptionText : undefined)}">
        ${!this.loggedIn ? html`
          <div class="ents-wishlist-hint-box" data-test-ents-mail-product-added-hint>
            <h3 class="ents-copy-bold">${this.messages['ents.wishlist.mail.product-added.headline']}</h3>
            <p class="ents-copy">
              ${this.messages['ents.wishlist.mail.product-added.text']}
              <br/>
              <br/>
              <a
                class="bdk-button" type="button" variant="outline" size="medium"
                href="${this.wishlistAppConfig.loginEndpointWithFavoritesReferrer}"
              >
                ${this.messages['ents.wishlist.empty.logintext']}
              </a>
            </p>
          </div>
        ` : nothing}
      </ents-wishlist-mail-product-added>
    `;
  }

  // Public method called programmatically from executeOnOriginPage
  public scrollToProduct(e: CustomEvent<ProductIdentifierContainer>) {
    this.resetAllFilters();
    const elementId = `ents-wishlist-product-${e.detail.identifier}`;
    setTimeout(() => {
      const element = this.shadowRoot?.getElementById(elementId);
      if (element) {
        element.scrollIntoView({behavior: 'smooth', block: 'center'});
        setTimeout(() => {
          const anchor = element.shadowRoot?.querySelector('a');
          if (anchor) {
            anchor.focus({preventScroll: true});
          }
        }, 150);
      }
    }, 150);
  }

  private renderWishlistGrid(products: Product[]): TemplateResult {
    setTimeout(() => this.updateProgressiveRenderCount(products), 15);
    const gridClass = this.wishlistAppConfig.productTileRedesign
      ? 'ents-wishlist-grid ents-wishlist-grid--redesign'
      : 'ents-wishlist-grid';

    const surveyHeadingClasses = this.wishlistAppConfig.productTileRedesign ? 'ents-heading ents-s:heading-small ents-m:heading' : 'ents-heading';
    const surveyTextClasses = this.wishlistAppConfig.productTileRedesign ? 'ents-copy-small ents-m:copy' : '';

    return html`
      <ents-options class="ents-options-container">
        ${this.renderFilterButton()}
      </ents-options>
      <div class="${gridClass}">
        ${repeat(
          products.slice(0, this.renderedCount),
          product => ProductIdentifier.createByProduct(product).id,
          (product, index) => this.renderSingleProduct(product, index, products.length),
        )}
        <div class="ents-inlist-survey" ?hidden="${!this.showSurvey()}">
          <button
            @click="${this.hideSurvey}"
            class="closeButton bdk-tw-button-icon"
            variant="just-icon"
            size="small"
            type="button"
            data-test-survey-close-button
            aria-label="Close"
          >
            <span role="presentation"> ${addPresentationToIcon(Close())} </span>
          </button>

          <div class="content">
            <div class="text">
              <span class="heart">${addPresentationToIcon(heartIcon())}</span>
              <h2 class="${surveyHeadingClasses}">Wie verwalten Sie Ihre Favoriten am
                liebsten?</h2>
              <p class="${surveyTextClasses}">Ihre Meinung zählt: Helfen Sie uns, Ihren Favoritenbereich zu
                verbessern.</p>
              <button @click="${this.surveyOpen}" class="bdk-button surveyButton" type="button" variant="outline"
                      size="small">
                <span>Umfrage starten</span>
              </button>
            </div>
          </div>
        </div>
        <div class="ents-inlist-newsletter" ?hidden="${!this.hasInlistNewsletterContent()}">
          <slot name="inlist-newsletter"></slot>
        </div>
      </div>
    `;
  }

  private updateProgressiveRenderCount(products: Product[]) {
    if (this.renderedCount < products.length) {
      this.renderedCount = Math.min(this.renderedCount + this.chunkSize, products.length);
    }
  }

  private hasInlistNewsletterContent(): boolean {
    const firstSlotElement = this.inlistNewsletterSlotElements[0]?.querySelector('slot');
    if (firstSlotElement instanceof HTMLSlotElement) {
      const firstRealSlottedElement = firstSlotElement.assignedElements()[0];
      if (!firstRealSlottedElement || firstRealSlottedElement?.getBoundingClientRect()?.height < 2) {
        return false;
      }
    }
    return true;
  }

  private showSurvey(): boolean {
    return this.wishlistAppConfig.surveyEnabled && !document.cookie.includes(`ents_wishlistSurvey=${this.SURVEY_VERSION}`);
  }

  private renderSingleProduct(product: Product, index: number, countListItems: number): TemplateResult {
    return html`
      <ents-wishlist-product
        id="ents-wishlist-product-${ProductIdentifier.createByProduct(product).id}"
        class="ents-wishlist-product${classMap({'ents-wishlist-product--redesign': this.wishlistAppConfig.productTileRedesign})}"
        selected-article-id="${product.articleId}"
        position="${index + 1}"
        ?model-view-enabled="${this.modelViewEnabled}"
        .product="${product}"
        .collection="${this.wishlistCollection}"
        data-test-ents-wishlist-product="${product.syan}"
        ?logged-in="${this.loggedIn}"
        list-count="${countListItems}"
        @aktionsflags:schedule-load=${this.handleScheduleLoadCampFlags}>
      </ents-wishlist-product>
    `;
  }

  private renderWishlistItemSlider(isEmtpyCollection: boolean) {
    if (!this.wishlistCollection) {
      return nothing;
    }
    return html`
      <ents-wishlist-item-slider
        .wishlistCollection="${this.wishlistCollection}"
        ?is-empty-collection="${isEmtpyCollection}"
      >
      </ents-wishlist-item-slider>`;
  }

  private renderCollectionHead() {
    const collectionTrackingContext: CollectionTrackingContext = {
      productsAmount: this.products.length,
      eventContext: 'collection_detail',
    };
    return html`
      <div class="ents-wishlist-collection-details">
        <a
          class="bdk-link-secondary"
          size="medium"
          href="${this.wishlistAppConfig.collectionEndpoint}"
          data-test-ents-wishlist-collection-backlink
        >
          ${addPresentationToIcon(arrowBackIcon())}
          ${this.messages['ents.wishlist.collection.details.back.link.text']}
        </a>
      </div>
      <ents-wishlist-collection-head
        .wishlistCollection="${this.wishlistCollection}"
        .collectionTrackingContext="${collectionTrackingContext}"
        @scroll-top="${this.onScrollTop}"
        with-headline
      >
      </ents-wishlist-collection-head>
    `;
  }

  protected render(): TemplateResult {
    const filteredProducts = this.filterMenu.filterAndSortProducts(this.products, false);
    const wishlistRenderCase = WishlistRenderCase.determineRenderCase(
      this.products,
      filteredProducts,
      this.wishlistCollection
    );
    const isEmtpyCollection = wishlistRenderCase.productsRenderCase === ProductsRenderCase.RenderCollectionEmptyUnfiltered;
    // empty div around head and products list is needed for the sticky header to stay inside the product grid
    return html`
      ${this.renderEventsIcon()}
      ${this.wishlistMailProductAdded ? this.renderMailProductAdded() : nothing}
      <div>
        ${this.renderCaseHead(wishlistRenderCase)}
        ${this.renderCaseProducts(wishlistRenderCase)}
      </div>
      ${this.renderWishlistItemSlider(isEmtpyCollection)}
    `;
  }

  private renderCaseHead(wishlistRenderCase: WishlistRenderCase) {
    if (wishlistRenderCase.headRenderCase === HeadRenderCase.RenderCollectionHead) {
      return this.renderCollectionHead();
    }
    return nothing;
  }

  private renderCaseProducts(wishlistRenderCase: WishlistRenderCase) {
    switch (wishlistRenderCase.productsRenderCase) {
      case ProductsRenderCase.RenderCollectionEmptyUnfiltered: {
        return this.renderEmptyCollection();
      }
      case ProductsRenderCase.RenderProducts: {
        return this.renderWishlist(wishlistRenderCase.products);
      }
      case ProductsRenderCase.RenderNothing: {
        // the empty view is defined in wishlist.peb and only toggled via CSS class
        // in this component (see "updated" and "toggleEmptyWishlist" functions)
        return nothing;
      }
      case ProductsRenderCase.RenderEmptyFiltered: {
        return this.renderOverfilteredWishlist();
      }
      case ProductsRenderCase.RenderEmptyFilteredExternalNotification: {
        // no products pass the filters, reset them and return all products in wishlist
        this.resetAllFilters();
        return this.renderWishlist(wishlistRenderCase.products);
      }
      default: {
        return this.renderWishlist(wishlistRenderCase.products);
      }
    }
  }

  protected updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);

    if (changedProperties.has('productsState')) {
      this.setProductsForCollection();
      this.reapplyFilter();
    } else if (changedProperties.has('wishlistCollection')) {
      this.setProductsForCollection();
    }
  }

  private renderChip(activeOption: OptionUIModel) {
    return html`
      <ents-filter-chip
        .option="${activeOption}"
        .isSort="false">
      </ents-filter-chip>`;
  }

  private handleOverfilteredChange(e: CustomEvent<{ optionId: string, allowRemove: boolean }>) {
    this.onWishlistFilterChangedEvent(e);
    this.onWishlistFilterApply();
  }

  private triggerCampEvent() {
    document.dispatchEvent(new CustomEvent('camp:aktionsflags:initialized'));
  }

  private surveyOpen() {
    this.setSurveyCookie();

    const surveyElement = this.shadowRoot?.querySelector('.ents-inlist-survey') as HTMLElement;
    if (surveyElement) {
      surveyElement.hidden = true;
    }
    document.dispatchEvent(
      new CustomEvent('empera:survey-ssi:open', {detail: {surveyCollectorId: this.wishlistAppConfig.surveyCollectorId}})
    );
  };

  private hideSurvey() {
    this.setSurveyCookie();
    const surveyElement = this.shadowRoot?.querySelector('.ents-inlist-survey') as HTMLElement;
    if (surveyElement) surveyElement.hidden = true;
  }

  private setSurveyCookie() {
    const expires = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toUTCString();
    document.cookie = `ents_wishlistSurvey=${this.SURVEY_VERSION}; expires=${expires}; path=/`;
  }

}
